import { Link } from 'react-router-dom';
import { Zap, Users, Lock } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import type { List, FilterSpec } from '../../types/list';

interface ListCardProps {
  list: List;
}

/** Relative for recent dates, short date otherwise — mirrors the mockup's "Updated 2d ago". */
function relativeOrShortDate(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 0) return 'just now';
  if (diffMs < 60 * 1000) return 'just now';
  if (diffMs < 60 * 60 * 1000) {
    const m = Math.floor(diffMs / (60 * 1000));
    return `${m}m ago`;
  }
  if (diffMs < day) {
    const h = Math.floor(diffMs / (60 * 60 * 1000));
    return `${h}h ago`;
  }
  if (diffMs < 7 * day) {
    const d = Math.floor(diffMs / day);
    return `${d}d ago`;
  }
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

/**
 * Up to ~4 minimal filter chips derived from filterSpec, to signal a saved filter.
 * Rich human-readable labels are Part 2 — keep these simple/raw.
 */
function smartChips(spec: FilterSpec | null | undefined): string[] {
  if (!spec) return [];
  const chips: string[] = [];
  if (spec.states && spec.states.length) chips.push(...spec.states);
  if (spec.sport) chips.push(spec.sport);
  if (spec.role) chips.push(spec.role);
  if (spec.ncesLevels && spec.ncesLevels.length) chips.push(...spec.ncesLevels);
  return chips.slice(0, 4);
}

export function ListCard({ list }: ListCardProps) {
  const me = useAuthStore((s) => s.user);
  const isSmart = list.kind === 'SMART';
  const shareCount = list.shares?.length ?? 0;
  const isShared = shareCount > 0;
  // On the Shared/Team tabs (someone else's list) show whose list it is instead of the count.
  const ownerLabel = list.owner && me && list.ownerId !== me.id ? (list.owner.displayName ?? 'a teammate') : null;
  const count = list.count ?? 0;
  const chips = isSmart ? smartChips(list.filterSpec) : [];

  return (
    <Link to={`/lists/${list.id}`} className="block">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow border-l-4 border-l-[#85ba12]">
        {/* Row 1: name + kind badge */}
        <div className="flex justify-between items-start gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 min-w-0">
            {list.name}
          </span>
          {isSmart ? (
            <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <Zap className="h-3 w-3" />
              SMART
            </span>
          ) : (
            <span className="flex-shrink-0 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300">
              MANUAL
            </span>
          )}
        </div>

        {/* Row 2: item count (manual) or filter chips (smart) */}
        {isSmart ? (
          chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {chips.map((chip, i) => (
                <span
                  key={`${chip}-${i}`}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full px-2 py-0.5 text-[11px]"
                >
                  {chip}
                </span>
              ))}
            </div>
          )
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
            {count} {count === 1 ? 'item' : 'items'}
          </div>
        )}

        {/* Row 3: share indicator · live/updated meta */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
          {isShared ? (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" />
              {ownerLabel ? `Shared by ${ownerLabel}` : `Shared · ${shareCount}`}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Private
            </span>
          )}
          <span>·</span>
          {isSmart ? (
            <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 font-medium">
              <Zap className="h-3 w-3" />
              {count} live
            </span>
          ) : (
            <span>Updated {relativeOrShortDate(list.updatedAt)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
